/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { createContext, useContext } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem as ShadcnMenuItem,
} from '@/shared/ui/components/shadcn/dropdown-menu';
import { cn } from '@/shared/ui/utils/tailwind';

export type MenuProps = {
  children: React.ReactNode;
  position?: 'bottom' | 'top' | 'left' | 'right' | 'bottom-end' | 'bottom-start' | 'top-end' | 'top-start';
  withArrow?: boolean;
  shadow?: string;
  width?: number | string;
  zIndex?: number;
  offset?: number;
};

export type MenuTargetProps = {
  children: React.ReactNode;
};

export type MenuDropdownProps = {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
};

export type MenuItemProps = {
  children: React.ReactNode;
  onClick?: () => void;
  [key: string]: any;
};

type MenuContextValue = {
  position?: string;
  width?: number | string;
  zIndex?: number;
  offset?: number;
};

const MenuContext = createContext<MenuContextValue>({});

/**
 * Parses a position string (e.g. "bottom-start") into side and align parts.
 *
 * @param position - Position string with optional alignment suffix.
 * @returns Object with side and align properties.
 */
function parsePosition(position?: string): { side?: 'top' | 'bottom' | 'left' | 'right'; align?: 'start' | 'end' | 'center' } {
  if (!position) return {};
  const parts = position.split('-');
  const side = parts[0] as 'top' | 'bottom' | 'left' | 'right';
  const align = (parts[1] as 'start' | 'end') || undefined;
  return { side, align };
}

/**
 * Menu adapter that delegates to shadcn DropdownMenu.
 */
const MenuComponent: React.FC<MenuProps> = ({ children, position, width, zIndex, offset }) => {
  return (
    <MenuContext.Provider value={{ position, width, zIndex, offset }}>
      <DropdownMenu>{children}</DropdownMenu>
    </MenuContext.Provider>
  );
};

/**
 * Wraps the trigger element for the dropdown.
 */
export const MenuTarget: React.FC<MenuTargetProps> = ({ children }) => {
  return <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>;
};

/**
 * Renders the dropdown content panel.
 */
export const MenuDropdown: React.FC<MenuDropdownProps> = ({ children, style, className }) => {
  const { position, width, zIndex, offset } = useContext(MenuContext);
  const { side, align } = parsePosition(position);

  const contentStyle: React.CSSProperties = {
    ...style,
    ...(width != null && { width: typeof width === 'number' ? `${width}px` : width, minWidth: typeof width === 'number' ? `${width}px` : width }),
    ...(zIndex != null && { zIndex }),
  };

  return (
    <DropdownMenuContent
      className={cn(className)}
      style={contentStyle}
      side={side}
      align={align}
      sideOffset={offset}
    >
      {children}
    </DropdownMenuContent>
  );
};

/**
 * Single menu item inside the dropdown.
 */
export const MenuItem: React.FC<MenuItemProps> = ({ children, onClick, ...props }) => {
  return (
    <ShadcnMenuItem onClick={onClick} {...props}>
      {children}
    </ShadcnMenuItem>
  );
};

const Menu = Object.assign(MenuComponent, {
  Target: MenuTarget,
  Dropdown: MenuDropdown,
  Item: MenuItem,
}) as typeof MenuComponent & {
  Target: typeof MenuTarget;
  Dropdown: typeof MenuDropdown;
  Item: typeof MenuItem;
};

export { Menu };
export default Menu;
